import type { ApiError } from '@contracts/common/ApiError';
import type { TransactionCurrency } from '@contracts/payment/Currency';
import type { PaymentError } from '@contracts/payment/PaymentError';
import { PaymentClient } from '@src/orders/services/PaymentClient';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

interface Props {
  onSuccess: () => void;
  onError: (message: string) => void;
  orderId: string;
  clientId: string;
}

export const usePayment = ({
  onSuccess,
  orderId,
  onError,
  clientId,
}: Props) => {
  const { t } = useTranslation();

  const handleError = ({ response: { data } }: ApiError<PaymentError>) => {
    const error = t(
      `makePayment.error.${data.error}`,
      t('makePayment.error.PAYMENT_FAILED'),
      { reason: data.message },
    );
    onError(error);
  };

  const { mutate: pay, isPending } = useMutation({
    mutationFn: async (props: {
      creditCardId: string;
      amount: number;
      currency: TransactionCurrency;
    }) => {
      await PaymentClient.payWithExistingCard({
        ...props,
        orderId,
        clientId,
      });
      return props.amount;
    },
    onSuccess: async () => {
      onSuccess();
    },
    onError: handleError,
  });

  return {
    pay,
    isLoading: isPending,
  };
};
